import {
  BuilderSplitRow,
  BuilderSplitSnapshotRow,
  ParsedBuilderSplitComment,
} from "./types";

export function createBuilderSplitSnapshot(
  rows: BuilderSplitRow[]
): BuilderSplitSnapshotRow[] {
  return rows.map((row) => ({
    builderId: row.builderId,
    employeeNumber: row.employeeNumber,
    split: row.split,
    deductionAmount: row.deductionAmount || 0,
  }));
}

export function hydrateRowsWithParsedComments(
  rows: BuilderSplitRow[],
  parsed: ParsedBuilderSplitComment
): BuilderSplitRow[] {
  return rows.map((row) => {
    const match = parsed.deductions.find((d) => d.empId === row.employeeNumber);

    if (!match) {
      return {
        ...row,
        deductionComment: row.deductionComment || "",
        deductionConfirmed: !!row.deductionComment,
      };
    }

    return {
      ...row,
      deductionComment: match.comment,
      deductionConfirmed: true,
    };
  });
}

export function computeSplitAmount(totalBonus: number, split: number): number {
  return parseFloat((totalBonus * (split / 100)).toFixed(2));
}

export function didDeductionContextChange(
  current: BuilderSplitRow,
  original?: BuilderSplitSnapshotRow
): boolean {
  if (!original) return true;

  return (
    current.builderId !== original.builderId ||
    current.employeeNumber !== original.employeeNumber ||
    (current.deductionAmount || 0) !== original.deductionAmount
  );
}

export function didSplitChange(
  current: BuilderSplitRow,
  original?: BuilderSplitSnapshotRow
): boolean {
  if (!original) return true;
  return current.split !== original.split;
}

export function getOriginalRow(
  current: BuilderSplitRow,
  originals: BuilderSplitSnapshotRow[]
): BuilderSplitSnapshotRow | undefined {
  return originals.find(
    (o) =>
      (current.builderId > 0 && o.builderId === current.builderId) ||
      (current.employeeNumber > 0 && o.employeeNumber === current.employeeNumber)
  );
}

export function hasAnyMeaningfulChanges(
  currentRows: BuilderSplitRow[],
  originalRows: BuilderSplitSnapshotRow[]
): boolean {
  if (currentRows.length !== originalRows.length) return true;

  return currentRows.some((row) => {
    const original = getOriginalRow(row, originalRows);
    if (!original) return true;

    return (
      row.builderId !== original.builderId ||
      row.employeeNumber !== original.employeeNumber ||
      row.split !== original.split ||
      (row.deductionAmount || 0) !== original.deductionAmount
    );
  });
}

export function getChangedDeductionRows(
  currentRows: BuilderSplitRow[],
  originalRows: BuilderSplitSnapshotRow[]
): BuilderSplitRow[] {
  return currentRows.filter((row) => {
    const original = getOriginalRow(row, originalRows);
    return didDeductionContextChange(row, original);
  });
}